import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Sun, BatteryCharging, BatteryFull, Home, Zap, Gauge,
  Activity, RefreshCw, AlertTriangle, PlugZap, Power, Plug
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 30000;

const fmtPower = (w) => {
  if (w === null || w === undefined) return '—';
  const abs = Math.abs(w);
  if (abs >= 1000) return `${(w / 1000).toFixed(1)} kW`;
  return `${Math.round(w)} W`;
};

const StatCard = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div
    className="rounded-2xl border border-border bg-card p-5 flex flex-col gap-2 transition-transform duration-200 hover:-translate-y-0.5"
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-muted-foreground">
      <Icon className={`h-4 w-4 ${accent}`} />
      <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
    </div>
    <div className="text-3xl font-bold tracking-tight tabular-nums">{value}</div>
    {sub && <div className="text-sm text-muted-foreground">{sub}</div>}
  </div>
);

export function SolarDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [controlEnabled, setControlEnabled] = useState(false);
  const timer = useRef(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/solar/stats`);
      setStats(res.data);
      setStatus('ok');
    } catch (e) {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/solar/generator`)
      .then((r) => setControlEnabled(!!r.data?.control_enabled))
      .catch(() => {});
  }, []);

  const toggleGenerator = useCallback(async (turnOn) => {
    setGenBusy(true);
    try {
      const res = await axios.post(`${API}/solar/generator`, { on: turnOn }, { withCredentials: true });
      const d = res.data || {};
      if (d.alert_only) {
        if (d.alert_sent) {
          toast.success(`Alert sent — go ${turnOn ? 'start' : 'stop'} the generator on-site`);
        } else {
          toast.warning('Alert recorded, but email isn’t configured yet');
        }
      } else {
        toast.success(turnOn ? 'Generator start command sent' : 'Generator stop command sent');
      }
      await fetchStats();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not send the request';
      toast.error(msg);
    } finally {
      setGenBusy(false);
      setConfirmOpen(false);
    }
  }, [fetchStats]);

  useEffect(() => {
    fetchStats();
    timer.current = setInterval(fetchStats, REFRESH_MS);
    return () => clearInterval(timer.current);
  }, [fetchStats]);

  if (status === 'loading' && !stats) {
    return (
      <div className="rounded-2xl border border-border bg-card p-8 text-center text-muted-foreground" data-testid="solar-loading">
        <RefreshCw className="h-5 w-5 mx-auto mb-2 animate-spin" />
        Connecting to your solar system…
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-6 text-center" data-testid="solar-error">
        <AlertTriangle className="h-5 w-5 mx-auto mb-2 text-amber-500" />
        <p className="font-medium">Solar data is unavailable right now.</p>
        <p className="text-sm text-muted-foreground mt-1">
          The dashboard couldn't reach the system. It will keep trying automatically.
        </p>
      </div>
    );
  }

  const s = stats || {};
  const soc = s.battery_soc;
  const charging = (s.battery_power ?? 0) >= 0;
  const grid = s.grid_power;
  const importing = (grid ?? 0) >= 0;
  const socColor = soc >= 60 ? 'bg-emerald-500' : soc >= 30 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="not-prose space-y-5" data-testid="solar-dashboard">
      {/* Status bar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${s.connected ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
          <span className="text-sm font-medium">{s.connected ? 'Live' : 'Offline'}</span>
          {s.system_state && (
            <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 text-xs" data-testid="solar-state">
              <Activity className="h-3 w-3" /> {s.system_state}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {s.demo && (
            <span className="rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/40 px-2.5 py-0.5 text-xs font-semibold" data-testid="solar-demo-badge">
              DEMO DATA
            </span>
          )}
          <button
            onClick={fetchStats}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid="solar-refresh-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Hero: Solar -> Battery/Home -> Grid */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-transparent p-6" data-testid="solar-pv">
          <div className="flex items-center gap-2 text-emerald-500">
            <Sun className="h-5 w-5" />
            <span className="text-xs font-semibold uppercase tracking-wide">Solar Now</span>
          </div>
          <div className="mt-3 text-4xl font-bold tabular-nums">{fmtPower(s.pv_power)}</div>
          <div className="text-sm text-muted-foreground mt-1">
            Today: {s.yield_today != null ? `${s.yield_today} kWh` : '—'}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6" data-testid="solar-battery">
          <div className="flex items-center gap-2 text-sky-500">
            {charging ? <BatteryCharging className="h-5 w-5" /> : <BatteryFull className="h-5 w-5" />}
            <span className="text-xs font-semibold uppercase tracking-wide">Battery</span>
          </div>
          <div className="mt-3 text-4xl font-bold tabular-nums">{soc != null ? `${soc}%` : '—'}</div>
          <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
            <div className={`h-full ${socColor} transition-all duration-500`} style={{ width: `${Math.max(0, Math.min(100, soc || 0))}%` }} />
          </div>
          <div className="text-sm text-muted-foreground mt-2">
            {charging ? 'Charging' : 'Discharging'} {fmtPower(Math.abs(s.battery_power ?? 0))}
            {s.battery_voltage != null && <> · {s.battery_voltage} V</>}
          </div>
        </div>

        <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-transparent p-6" data-testid="solar-load">
          <div className="flex items-center gap-2 text-amber-500">
            <Home className="h-5 w-5" />
            <span className="text-xs font-semibold uppercase tracking-wide">Home Load</span>
          </div>
          <div className="mt-3 text-4xl font-bold tabular-nums">{fmtPower(s.load_power)}</div>
          <div className="text-sm text-muted-foreground mt-1">Current consumption</div>
        </div>
      </div>

      {/* Secondary stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={PlugZap}
          label="Grid"
          value={grid != null ? fmtPower(Math.abs(grid)) : '—'}
          sub={grid != null ? (importing ? 'Importing' : 'Exporting') : 'No grid'}
          accent={importing ? 'text-red-500' : 'text-emerald-500'}
          testid="solar-grid"
        />
        {s.dc_load_available && (
          <StatCard
            icon={Plug}
            label="Inverter DC Load"
            value={s.dc_load_power != null ? fmtPower(Math.abs(s.dc_load_power)) : '—'}
            sub={s.dc_load_current != null ? `${Math.abs(s.dc_load_current)} A from battery` : 'DC draw'}
            accent="text-violet-500"
            testid="solar-dc-load"
          />
        )}
        <StatCard
          icon={Zap}
          label="Battery Current"
          value={s.battery_current != null ? `${Math.abs(s.battery_current)} A` : '—'}
          sub={charging ? 'Into battery' : 'From battery'}
          accent="text-sky-500"
          testid="solar-batt-current"
        />
        <StatCard
          icon={Gauge}
          label="Battery Voltage"
          value={s.battery_voltage != null ? `${s.battery_voltage} V` : '—'}
          accent="text-sky-500"
          testid="solar-voltage"
        />
        <StatCard
          icon={Sun}
          label="Yield Today"
          value={s.yield_today != null ? `${s.yield_today} kWh` : '—'}
          accent="text-emerald-500"
          testid="solar-yield"
        />
      </div>

      {/* Charge controllers */}
      {s.chargers && s.chargers.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="solar-chargers">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-emerald-500">
              <Sun className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wide">Charge Controllers</span>
            </div>
            {s.charger_current_total != null && (
              <span className="text-sm" data-testid="solar-charger-total">
                Combined <span className="font-semibold tabular-nums">{s.charger_current_total} A</span>
              </span>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {s.chargers.map((c, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border border-border px-4 py-3" data-testid={`solar-charger-${i}`}>
                <span className="text-sm font-medium">{c.name}</span>
                <span className="text-sm text-muted-foreground tabular-nums">
                  {c.current != null ? `${c.current} A` : '—'}
                  {c.power != null && <> · {Math.round(c.power)} W</>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Generator control */}
      {s.generator_available && (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="solar-generator">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${s.generator_running ? 'bg-emerald-500/15 text-emerald-500' : 'bg-muted text-muted-foreground'}`}>
                <Power className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-semibold uppercase tracking-wide">Generator</div>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="solar-generator-status">
                  <span className={`h-2 w-2 rounded-full ${s.generator_running ? 'bg-emerald-500 animate-pulse' : 'bg-muted-foreground/50'}`} />
                  {s.generator_running ? 'Running (assisting)' : 'Stopped'}
                </div>
              </div>
            </div>
            {isAdmin ? (
              <button
                onClick={() => setConfirmOpen(true)}
                disabled={genBusy}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 ${
                  s.generator_running
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-emerald-500 text-white hover:bg-emerald-600'
                }`}
                data-testid="solar-generator-toggle-btn"
              >
                <Power className="h-4 w-4" />
                {genBusy
                  ? 'Sending…'
                  : controlEnabled
                    ? (s.generator_running ? 'Stop Generator' : 'Start Generator')
                    : (s.generator_running ? 'Request Stop' : 'Request Start')}
              </button>
            ) : (
              <span className="text-xs text-muted-foreground" data-testid="solar-generator-admin-note">
                Admin sign-in required to control
              </span>
            )}
          </div>
          {isAdmin && !controlEnabled && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400" data-testid="solar-generator-safety-note">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>Safety mode: this only sends you an alert. The generator is not controlled remotely — start/stop it manually on-site.</span>
            </div>
          )}
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="solar-generator-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {controlEnabled
                ? (s.generator_running ? 'Stop the generator?' : 'Start the generator?')
                : (s.generator_running ? 'Send a generator STOP alert?' : 'Send a generator START alert?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {controlEnabled
                ? (s.generator_running
                    ? 'This sends a manual STOP command to the Cerbo generator start/stop service. The generator will shut down and stop charging/assisting.'
                    : 'This sends a manual START command to the Cerbo generator start/stop service. The generator will run, bring in grid power, and charge the batteries.')
                : `This will NOT ${s.generator_running ? 'stop' : 'start'} the generator. It only emails you an alert so you can ${s.generator_running ? 'stop' : 'start'} it manually on-site.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="solar-generator-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => toggleGenerator(!s.generator_running)}
              className={s.generator_running ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-500 hover:bg-emerald-600'}
              data-testid="solar-generator-confirm-action"
            >
              {controlEnabled
                ? (s.generator_running ? 'Yes, stop it' : 'Yes, start it')
                : 'Send alert'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <p className="text-xs text-muted-foreground text-center">
        Updates every 30s while this page is open{s.demo ? ' · showing simulated data until the Cerbo is connected' : ''}.
      </p>
    </div>
  );
}
